import { useState } from 'react';
import { useDefinedParams } from '../hooks/useDefinedParams';
import { getSiteErrorType, getSiteSuccessType, UserPermissions } from '../../../APIDocs/apiTypes';
import { RestTypes, useJbpQuery } from '../hooks/useJbpQuery';
import { Logger } from '../Logger';
import { useAppDispatch, useAppSelector } from '../store';
import { useUserInfo } from '@/hooks/useUserInfo';
import { EServiceLine, serviceLinesRequiringContractId, setSite } from '@/reducers/jbpGeneralSlice';
import { SiteDetail } from './siteDetails/SiteDetail';
import { MeterDetail } from './siteDetails/MeterDetail';
import { JobHistory } from './siteDetails/JobHistory';
import { Title } from '@portal_components/components/Title';

export const Site = () => {
  const dispatch = useAppDispatch();
  const { isInternalUser, canAccess } = useUserInfo();
  const { contractDisplayName, contractId } = useAppSelector(state => state.jbp);
  const [mpan, serviceLine] = useDefinedParams(['mpan', 'serviceLine']);
  const [dataErrorOccurred, setDataErrorOccurred] = useState(false);

  const onSiteData = (siteData: getSiteSuccessType) => {
    console.log('Site data loaded successfully', JSON.stringify(siteData), mpan);
    dispatch(setSite({ ...siteData, serviceLine: serviceLine as EServiceLine }));
  };

  const onSiteDataError = (err: getSiteErrorType) => {
    Logger.Log(`Data found to not exist -> ${err?.msg}`);
    setDataErrorOccurred(true);
  };

  const serviceLineHasContractId = (serviceLine: string): boolean => {
    return serviceLinesRequiringContractId.includes(serviceLine as EServiceLine);
  };

  const {
    isLoading: siteDataLoading,
    error: siteError,
    data: siteData
  } = useJbpQuery<getSiteSuccessType, getSiteErrorType>(
    RestTypes.Get,
    undefined,
    [`site-${mpan}-${serviceLine}-${contractId}`],
    `jbp/${mpan}/site`,
    true,
    serviceLine,
    onSiteData,
    onSiteDataError,
    600000,
    600000
  );

  try {
    const permissionsMap: { [key: string]: keyof UserPermissions } = {
      BNO: 'canUseBNOServiceLine',
      DNO: 'canUseDNOServiceLine',
      HH: 'canUseHHServiceLine',
      NHH: 'canUseNHHServiceLine',
      SUBMETER: 'canUseSubmeterServiceLine',
      MSS: 'canUseMSSServiceLine',
      MSA: 'canUseMSAServiceLine'
    };

    const permissionKey = permissionsMap[serviceLine];
    const hasAccess = canAccess(permissionKey);

    if (permissionKey == undefined || hasAccess == undefined || hasAccess === false) {
      return <div>You do not have permissions to view this Service line: {serviceLine}</div>;
    }
  } catch (error) {
    if (error instanceof SyntaxError) {
      //ignore failed parsing, will sort itself out on re-render
      //console.warn('Failed to parse user perms, this is expected until the model is populated at which point a re-render will happen and be successful');
    } else {
      console.error(error);
    }
  }

  return (
    <div id="jbp-site-screen" className="mx-10 mt-6">
      {/* Header */}
      <div className="mb-4">
        <Title text={`MPAN: ${mpan}`} />
        {serviceLineHasContractId(serviceLine) && isInternalUser && contractDisplayName && <Title className="mt-0 mb-0" text={`Contract: ${contractDisplayName}`} />}
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-12 gap-2 mt-8 pb-10">
        {/* First Column */}
        <div className="col-span-3 lg:col-span-2 grid grid-cols-1 gap-6">
          <SiteDetail loading={siteDataLoading} data={siteData} error={siteError} dataErrorOccurred={dataErrorOccurred} />
        </div>
        {/* Second Column */}
        <div className="col-span-3 lg:col-span-2 grid grid-cols-1 gap-6">
          <MeterDetail loading={siteDataLoading} meters={siteData?.Meters} error={siteError} dataErrorOccurred={dataErrorOccurred} />
        </div>

        {/* Main Content */}
        <div className="col-start-1 row-start-1 lg:col-start-5 col-span-full lg:col-span-8 grid grid-cols-1 gap-6">
          <JobHistory mpan={mpan} serviceLine={serviceLine} loading={siteDataLoading} siteData={siteData} dataErrorOccurred={dataErrorOccurred} />
        </div>
      </div>
    </div>
  );
};
